import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Button, Card, Spinner, ProgressBar } from "react-bootstrap";
import Flex from "components/common/Flex";
import cloudUpload from "assets/img/icons/cloud-upload.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faEye } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import IconButton from "components/common/IconButton";
import { Trash } from "react-bootstrap-icons";
import { useLocation, useSearchParams } from "react-router-dom";
import { handleFillStepData } from "features/cliams/config/claimSlice";
import {
  compressPDF,
  toBase64,
  compressImage,
  formatFileSize,
} from "../../utils";

const ClaimDocumentRequest = ({
  watch,
  setValue,
  clearErrors,
  setError,
  isDisabled = false,
}) => {
  const dispatch = useDispatch();
  const { viewClaimInfo } = useSelector((state) => state.claim);
  const location = useLocation();
  const isViewRequest = location.pathname.includes("view-claim-request");
  const [searchParams] = useSearchParams();
  const payloadClaim = viewClaimInfo?.payload_json;
  const attachments = Object.values(payloadClaim?.sup_info_attch || {});
  const invoiceAttach = viewClaimInfo?.invoice_attach;
  const cafAttach = viewClaimInfo?.caf_attach;
  const [files, setFiles] = useState([]);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [uploadError, setUploadError] = useState(false);
  const [compressionError, setCompressionError] = useState(null);

  useEffect(() => {
    let prefilledFiles = [];
    let fixedFiles = [];

    if (attachments.length) {
      prefilledFiles = attachments
        .filter((entry) => entry?.contentType && entry?.data && entry?.title)
        .map((entry, index) => {
          const mime = entry.contentType || "application/pdf";
          let blob;
          try {
            blob = new Blob(
              [Uint8Array.from(atob(entry.data), (c) => c.charCodeAt(0))],
              { type: mime }
            );
          } catch (error) {
            console.error(
              `Error decoding base64 for file ${entry.title}:`,
              error
            );
            return null;
          }

          const file = new File([blob], entry.title, {
            type: mime,
            lastModified: new Date(entry.creation || Date.now()).getTime(),
          });

          return {
            originalFile: file,
            compressedFile: file,
            preview: mime.includes("pdf") ? null : URL.createObjectURL(file),
            type: mime,
            path: entry.title,
            base64: entry.data,
            title: entry.title,
            creation: entry.creation || new Date().toISOString(),
          };
        })
        .filter((file) => file !== null);
    }

    if (invoiceAttach) {
      fixedFiles.push({
        originalFile: null,
        compressedFile: null,
        preview: null,
        type: "application/pdf",
        path: invoiceAttach,
        base64: null,
        title: "Invoice Attachment",
        creation: new Date().toISOString(),
        url: invoiceAttach,
        isFixed: true,
      });
    }

    if (cafAttach) {
      fixedFiles.push({
        originalFile: null,
        compressedFile: null,
        preview: null,
        type: "application/pdf",
        path: cafAttach,
        base64: null,
        title: "CAF Attachment",
        creation: new Date().toISOString(),
        url: cafAttach,
        isFixed: true,
      });
    }

    setFiles([...prefilledFiles, ...fixedFiles]);
    const attachmentsParsed = prefilledFiles.map((f) => ({
      valueAttachment: {
        contentType: f.type,
        title: f.title,
        creation: f.creation,
        data: f.base64,
      },
    }));
    setValue("attachments", attachmentsParsed);
    dispatch(
      handleFillStepData({ check: "attachments", value: attachmentsParsed })
    );

    return () => {
      prefilledFiles.forEach(
        (file) => file.preview && URL.revokeObjectURL(file.preview)
      );
      fixedFiles.forEach(
        (file) => file.preview && URL.revokeObjectURL(file.preview)
      );
    };
  }, [payloadClaim, viewClaimInfo, setValue, dispatch]);

  const handleView = (file) => {
    if (file.url) {
      window.open(file.url, "_blank");
      return;
    }

    try {
      const dataUrl = `data:${file.type || "application/pdf"};base64,${
        file.base64
      }`;
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <body style="margin:0">
              ${
                file.type.includes("pdf")
                  ? `<embed src="${dataUrl}" type="${file.type}" width="100%" height="100%" />`
                  : `<img src="${dataUrl}" style="width:100%;height:auto;" />`
              }
            </body>
          </html>
        `);
        newWindow.document.title = file.title || "Document Preview";
      } else {
        console.error("Failed to open new window. Ensure pop-ups are allowed.");
      }
    } catch (error) {
      console.error("Error opening file preview:", error);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [".jpeg", ".png", ".jpg"],
      "application/pdf": [".pdf"],
    },
    onDrop: async (acceptedFiles) => {
      setCompressionProgress(10);
      setUploadError(null);

      try {
        const processedFiles = await Promise.all(
          acceptedFiles.map(async (originalFile, index) => {
            let compressedFile = originalFile;

            if (originalFile.type === "application/pdf") {
              setCompressionProgress(40);
              compressedFile = await compressPDF(originalFile);
              setCompressionProgress(80);
            } else if (originalFile.type.startsWith("image/")) {
              compressedFile = await compressImage(originalFile);
            }

            // Check compressed file size (10MB limit)
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            if (compressedFile.size > maxSize) {
              throw new Error(
                `Compressed file "${
                  originalFile.name
                }" exceeds 10MB limit (${formatFileSize(compressedFile.size)})`
              );
            }

            const base64Data = await toBase64(compressedFile);

            const ext = originalFile.name
              .substring(originalFile.name.lastIndexOf("."))
              .toLowerCase();
            const creation = new Date().toISOString().split("T")[0];
            const hash = `${Date.now()}-${index}`;
            const title = `${hash}${ext}`;

            return {
              originalFile,
              compressedFile,
              preview: compressedFile.type.includes("pdf")
                ? null
                : URL.createObjectURL(compressedFile),
              type: compressedFile.type,
              path: title,
              base64: base64Data,
              title,
              creation,
            };
          })
        );

        const attachments = processedFiles.map((f) => ({
          valueAttachment: {
            contentType: f.compressedFile.type,
            title: f.title,
            creation: f.creation,
            data: f.base64,
          },
        }));

        setValue("attachments", attachments);
        dispatch(
          handleFillStepData({ check: "attachments", value: attachments })
        );
        setFiles((prev) => [...prev, ...processedFiles]);
        clearErrors("attachments");
        setCompressionProgress(100);
      } catch (error) {
        console.error("Compression error:", error);
        setUploadError(error.message || "Error processing files");
        setError("attachments", {
          type: "manual",
          message: error.message || "Error processing files",
        });
        setCompressionProgress(0);
      }
    },
    onDropRejected: (rejectedFiles) => {
      const errorMessage = rejectedFiles
        .map((file) =>
          file.errors
            .map((err) =>
              err.code === "file-invalid-type"
                ? `Invalid file type for "${file.file.name}"`
                : `File "${file.file.name}" is too large`
            )
            .join(", ")
        )
        .join("; ");
      setUploadError(errorMessage);
      setError("attachments", {
        type: "manual",
        message: errorMessage,
      });
      setCompressionProgress(0);
    },
    // Remove maxSize restriction here to allow larger files before compression
  });

  const handleRemove = (path) => {
    const newFiles = files.filter((file) => file.path !== path);
    setFiles(newFiles);
    const updatedAttachments = newFiles
      .filter((f) => !f.isFixed)
      .map((f) => ({
        valueAttachment: {
          contentType: f.type,
          title: f.title,
          creation: f.creation,
          data: f.base64 || "",
        },
      }));
    setValue("attachments", updatedAttachments);
    dispatch(
      handleFillStepData({ check: "attachments", value: updatedAttachments })
    );
  };

  useEffect(() => {
    return () =>
      files.forEach(
        (file) => file.preview && URL.revokeObjectURL(file.preview)
      );
  }, [files]);

  return (
    <Card className="mt-3">
      <Card.Header as="h6" className="bg-body-tertiary fs-12">
        Attach Document
      </Card.Header>
      <Card.Body>
        {!isViewRequest && (isDisabled || true) && (
          <div {...getRootProps({ className: "dropzone-area py-4" })}>
            <input {...getInputProps({ name: "attachments" })} />
            <div className={isViewRequest ? "fs-12" : "fs-10"}>
              {compressionProgress > 0 && compressionProgress < 100 ? (
                <div className="text-center">
                  <Spinner animation="border" variant="primary" size="sm" />
                  <ProgressBar
                    now={compressionProgress}
                    label={`${compressionProgress}%`}
                    className="mt-2"
                  />
                  <p className="mt-2 mb-0">Compressing files...</p>
                </div>
              ) : (
                <>
                  <img src={cloudUpload} alt="" width={25} className="me-2" />
                  <span className="d-none d-lg-inline">
                    Drag your file here (PDF or Image)
                    <br />
                    or,{" "}
                    <Button
                      variant="link"
                      size="sm"
                      className={isViewRequest ? "p-0 fs-12" : "p-0 fs-10"}
                    >
                      Browse
                    </Button>
                  </span>
                </>
              )}
            </div>
          </div>
        )}
        {compressionError && (
          <div className="text-danger mt-2 fs-10">{compressionError}</div>
        )}
        <div>
          {files.map((file) => (
            <Flex
              alignItems="center"
              className="py-2 border-bottom btn-reveal-trigger"
              key={file.path}
            >
              {file.type.includes("pdf") ? (
                <div
                  className="rounded d-flex align-items-center justify-content-center"
                  style={{ width: 20, height: 20 }}
                >
                  <FontAwesomeIcon icon={faFilePdf} className="text-danger" />
                </div>
              ) : (
                <img
                  className="rounded"
                  width={20}
                  height={20}
                  src={file.preview}
                  alt={file.title}
                />
              )}
              <Flex
                justifyContent="between"
                alignItems="center"
                className="ms-3 flex-1"
              >
                <div>
                  <h6 className={isViewRequest ? "mb-0 fs-12" : "mb-0 fs-9"}>
                    {file.originalFile ? file.originalFile.name : file.title}
                  </h6>
                  {file.compressedFile && (
                    <small className="text-muted">
                      Size: {formatFileSize(file.compressedFile.size)}
                    </small>
                  )}
                </div>
              </Flex>
              <IconButton
                variant="primary"
                size="sm"
                onClick={() => handleView(file)}
                className="ms-2"
              >
                <FontAwesomeIcon size={isViewRequest ? 12 : 16} icon={faEye} />
              </IconButton>
              {!file.isFixed && !isViewRequest && (
                <IconButton
                  variant="danger"
                  size="sm"
                  onClick={() => handleRemove(file.path)}
                  className="ms-2"
                >
                  <Trash size={isViewRequest ? 12 : 16} />
                </IconButton>
              )}
            </Flex>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ClaimDocumentRequest;
